import type { ReactNode } from "react";

import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";

interface ResponsiveDialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  headerAction?: ReactNode;
  contentClassName?: string;
  bodyClassName?: string;
  compact?: boolean;
  titleAlign?: "center" | "start";
}

export function ResponsiveDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  headerAction,
  contentClassName,
  bodyClassName,
  compact = false,
  titleAlign = "center",
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(value) => !value && onClose()}>
        <DrawerContent showHandle className="tw:max-h-[90vh]">
          <DrawerHeader className="tw:relative tw:shrink-0 tw:border-b tw:border-border tw:px-5 tw:pb-4 tw:pt-2">
            {headerAction && (
              <div className="tw:absolute tw:start-5 tw:top-1/2 tw:z-10 tw:-translate-y-1/2">
                {headerAction}
              </div>
            )}
            <DrawerTitle className="tw:text-center tw:text-lg tw:font-semibold">
              {title}
            </DrawerTitle>
            {description && (
              <DrawerDescription className="tw:text-center">
                {description}
              </DrawerDescription>
            )}
          </DrawerHeader>
          {children != null && (
            <div
              className={cn(
                "tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-5",
                compact ? "tw:py-3" : "tw:py-5",
                bodyClassName,
              )}
            >
              {children}
            </div>
          )}
          {footer && (
            <DrawerFooter className="tw:shrink-0 tw:flex-col-reverse tw:gap-2 tw:border-t tw:border-border tw:px-5 tw:pb-5 tw:pt-4">
              {footer}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()} modal>
      <DialogContent
        className={cn(
          "tw:flex tw:max-h-[calc(100dvh-2rem)] tw:w-[calc(100vw-2rem)] tw:max-w-180 tw:flex-col tw:gap-0 tw:overflow-hidden tw:rounded-[28px] tw:border-0 tw:bg-background tw:p-0 tw:shadow-2xl",
          contentClassName,
        )}
      >
        <div className="tw:relative tw:shrink-0 tw:border-b tw:border-border tw:px-6 tw:pb-5 tw:pt-6">
          {headerAction && (
            <div className="tw:absolute tw:start-6 tw:top-1/2 tw:z-10 tw:-translate-y-1/2">
              {headerAction}
            </div>
          )}
          <DialogCloseButton
            onClick={onClose}
            className="tw:absolute tw:end-6 tw:top-1/2 tw:z-10 tw:-translate-y-1/2"
          />
          <DialogHeader
            className={cn(
              "tw:gap-1",
              titleAlign === "start" ? "tw:items-start" : "tw:items-center",
            )}
          >
            <DialogTitle
              hideClose
              className={cn(
                "tw:w-full tw:text-xl tw:font-semibold",
                titleAlign === "start"
                  ? "tw:pe-10 tw:text-start"
                  : "tw:text-center",
              )}
            >
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription
                className={
                  titleAlign === "start" ? "tw:text-start" : "tw:text-center"
                }
              >
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
        </div>

        {children != null && (
          <div
            className={cn(
              "tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-6",
              compact ? "tw:py-3" : "tw:pb-6 tw:pt-5",
              bodyClassName,
            )}
          >
            {children}
          </div>
        )}

        {footer && (
          <DialogFooter className="tw:shrink-0 tw:flex-row tw:border-t tw:border-border tw:px-6 tw:pb-6 tw:pt-4 tw:*:flex-1">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
